/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import authentication from './authentication';
import authenticationToken from './authentication-token';
import blueprint from './blueprint';
import blueprintQuestions from '../slice/blueprintQuestions';
import initiativeBlueprintQuestions from './initiative-blueprint-questions';
import common from './common';
import currentUser from './current-user';
import customMetricGroups from './custom-metric-groups';
import framework from './framework';
import globalData from './global-data';
import industrySectors from './industry-sectors';
import initiative from './initiative';
import insightsSlice from '../slice/insightsSlice';
import onboardingToken from './onboarding-token';
import peerComparison from './peerComparison';
import publicScorecard from '../slice/publicScorecard';
import reportingFrameworks from './reporting-frameworks';
import scorecard from './scorecard';
import scorecardSurvey from './scorecard-survey';
import siteAlerts from '../slice/siteAlertsSlice';
import survey from './survey';
import surveyConfig from '../slice/surveyConfig';
import surveyAvailable from '../slice/surveyAvailable';
import surveyDelegationUsers from '../slice/surveyDelegationUsers';
import surveyListSummary from './survey-list-summary';
import surveySettings from '../slice/surveySettings';
import tour from './tour';
import universalTrackers from './universal-trackers';
import universalTrackerGroups from './universal-tracker-groups';
import universalTrackerModal from './universal-tracker-modal';
import universalTrackerValueDetails from './universal-tracker-value-details';
import universalTrackerValues from './universal-tracker-values';
import utrvChartData from '../slice/utrvChartDataSlice';
import UNSDGMap from './unsdg-map';
import upgradeModal from './upgrade-modal';
import userTrackers from './user-trackers';
import userTrackersSummary from './user-trackers-summary';
import valueListSlice from '../slice/valueListSlice';
import welcomeModal from './welcomeModal';
import customMetricSurveyGroups from '../slice/metricGroupsSlice';
import initiativeSurveyListSlice from '../slice/initiativeSurveyListSlice';
import portfolioTreeSlice from '../slice/portfolioTreeSlice';
import portfolioSlice from '../slice/portfolioSlice';
import glossarySlice from '../slice/glossarySlice';
import rootInitiativeListSlice from '../slice/rootInitiativeListSlice';
import referenceUtrSlice from '../slice/referenceUtrSlice';
import { configureStore, AnyAction, ThunkAction } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { referralsApi } from '../api/referrals';
import { utrvApi } from '../api/utrv';
import { initiativeApi } from '../api/initiative-stats';
import { g17ecoApi } from '../api/g17ecoApi';
import portfolioCompanySlice from '../slice/portfolioCompanySlice';

import { bookmarksApi } from '../api/bookmarks';
import { surveyTemplatesApi } from '../api/survey-templates';
import { surveyTemplatesHistoryApi } from '../api/survey-templates-history';
import { benchmarkingApi } from '../api/benchmarking';
import { defaultSurveyConfigApi } from '../api/default-survey-config';
import { bankingSettingsApi } from '../api/banking-settings';
import { adminDashboardApi } from '../api/admin-dashboard';
import { metricGroupsApi } from '../api/metric-groups';
import { carbonCalculatorsApi } from '../apps/carbon-calculator/api';
import { materialityAssessmentApi } from '../apps/materiality-assessment/api/materiality-assessment';

export const reducer = {
  [g17ecoApi.reducerPath]: g17ecoApi.reducer,
  [referralsApi.reducerPath]: referralsApi.reducer,
  [utrvApi.reducerPath]: utrvApi.reducer,
  [initiativeApi.reducerPath]: initiativeApi.reducer,
  [bookmarksApi.reducerPath]: bookmarksApi.reducer,
  [surveyTemplatesApi.reducerPath]: surveyTemplatesApi.reducer,
  [surveyTemplatesHistoryApi.reducerPath]: surveyTemplatesHistoryApi.reducer,
  [benchmarkingApi.reducerPath]: benchmarkingApi.reducer,
  [defaultSurveyConfigApi.reducerPath]: defaultSurveyConfigApi.reducer,
  [bankingSettingsApi.reducerPath]: bankingSettingsApi.reducer,
  [adminDashboardApi.reducerPath]: adminDashboardApi.reducer,
  [metricGroupsApi.reducerPath]: metricGroupsApi.reducer,
  [carbonCalculatorsApi.reducerPath]: carbonCalculatorsApi.reducer,
  [materialityAssessmentApi.reducerPath]: materialityAssessmentApi.reducer,
  authentication,
  authenticationToken,
  blueprint,
  blueprintQuestions,
  common,
  currentUser,
  customMetricGroups,
  customMetricSurveyGroups,
  framework,
  globalData,
  industrySectors,
  initiative,
  initiativeBlueprintQuestions,
  initiativeSurveyList: initiativeSurveyListSlice,
  insightsData: insightsSlice,
  onboardingToken,
  peerComparison,
  publicScorecard,
  reportingFrameworks,
  referenceUtr: referenceUtrSlice,
  scorecard,
  scorecardSurvey,
  siteAlerts,
  survey,
  surveyConfig,
  surveyAvailable,
  surveyDelegationUsers,
  surveyListSummary,
  tour,
  universalTrackers,
  universalTrackerGroups,
  universalTrackerModal,
  universalTrackerValues,
  universalTrackerValueDetails,
  utrvChartData,
  UNSDGMap,
  upgradeModal,
  userTrackers,
  userTrackersSummary,
  valueList: valueListSlice,
  welcomeModal,
  surveySettings,
  portfolioTree: portfolioTreeSlice,
  portfolio: portfolioSlice,
  portfolioCompany: portfolioCompanySlice,
  rootInitiatives: rootInitiativeListSlice,
  glossary: glossarySlice,
};

const isProd = process.env.NODE_ENV === 'production';
export const ReduxStore = configureStore({
  reducer,
  devTools: !isProd,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: Boolean(import.meta.env.REACT_APP_REDUX_SERIALIZABLE_CHECK),
      immutableCheck: !isProd && import.meta.env.REACT_APP_REDUX_IMMUTABLE_CHECK ? { warnAfter: 100 } : false,
    })
      .concat(referralsApi.middleware)
      .concat(g17ecoApi.middleware)
      .concat(utrvApi.middleware)
      .concat(initiativeApi.middleware)
      .concat(bookmarksApi.middleware)
      .concat(surveyTemplatesApi.middleware)
      .concat(surveyTemplatesHistoryApi.middleware)
      .concat(benchmarkingApi.middleware)
      .concat(defaultSurveyConfigApi.middleware)
      .concat(bankingSettingsApi.middleware)
      .concat(adminDashboardApi.middleware)
      .concat(metricGroupsApi.middleware)
      .concat(carbonCalculatorsApi.middleware)
      .concat(materialityAssessmentApi.middleware),
});

export type RootState = ReturnType<typeof ReduxStore.getState>;
export type AppDispatch = typeof ReduxStore.dispatch;
export type DefaultThunk = ThunkAction<void, RootState, any, AnyAction>;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export interface ReduxReducer<T = any[]> {
  data: T;
  loaded: boolean;
  loading?: boolean;
  errored: boolean;
  lastUpdated?: number;
  errorMessage?: string;
}

export type ReduxStateLoadable<LoadedData = unknown, DefaultData = undefined> =
  | (ReduxReducer<DefaultData> & { loaded: false })
  | (ReduxReducer<LoadedData> & { loaded: true });
